import React, { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react';
import { useTheme, alpha } from '@mui/material/styles';
import { List, ListItem, ListItemButton, ListItemIcon, ListItemText, Stack, Collapse, TextField, InputAdornment, IconButton, Tooltip, Box } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { sidebarNavItems as MenuItemLists } from '../../constant/navItems';
import { ClearRounded, ExpandLess, ExpandMore, SearchRounded, FiberManualRecord } from '@mui/icons-material';

const generateKey = (item, parentIndex = '', level = 0) => {
    return `${parentIndex}-${level}-${item.menu}`.replace(/\s+/g, '-');
};

export default function MenuContent({ sidebarCollapsed = false, onMenuSelect }) {
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const { t } = useTranslation();

    const computeOpenStates = useCallback(() => {
        const states = {};
        const findActiveParents = (items, level = 0, parentKey = '') => {
            items.forEach((item) => {
                const itemKey = generateKey(item, parentKey, level);
                if (item.children && item.children.length > 0) {
                    const hasActiveChild = findActiveParents(item.children, level + 1, itemKey);
                    if (hasActiveChild) {
                        states[itemKey] = true;
                    }
                }
            });
            return items.some((item) => {
                const path = location.pathname;
                const itemPath = item.route;
                return path === itemPath || path === `${itemPath}/` || (itemPath !== '/' && path.startsWith(`${itemPath}/`));
            });
        };
        findActiveParents(MenuItemLists);
        return states;
    }, [location.pathname]);

    const [openStates, setOpenStates] = useState(computeOpenStates);
    const [searchTerm, setSearchTerm] = useState('');

    const activeItemRef = useRef(null);
    const menuItemDomRefs = useRef({});

    const getMenuText = (menuKey) => t(menuKey, menuKey);

    const isActive = useCallback((item) => {
        const path = location.pathname;
        const itemPath = item.route;
        return path === itemPath || path === `${itemPath}/` || (itemPath !== '/' && path.startsWith(`${itemPath}/`));
    }, [location.pathname]);

    useLayoutEffect(() => {
        setOpenStates(prev => {
            const next = computeOpenStates();
            const hasChanges = Object.keys(next).some(key => !prev[key]) || Object.keys(prev).some(key => !(key in next));
            if (hasChanges) {
                return { ...prev, ...next };
            }
            return prev;
        });
    }, [location.pathname, computeOpenStates]);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (activeItemRef.current) {
                activeItemRef.current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                });
            }
        }, 150);
        return () => clearTimeout(timer);
    }, [location.pathname, sidebarCollapsed]);

    const handleClick = (key, item) => {
        if (item.children && item.children.length > 0) {
            if (!sidebarCollapsed) {
                const willOpen = !openStates[key];
                setOpenStates(prev => ({ ...prev, [key]: !prev[key] }));

                if (willOpen) {
                    setTimeout(() => {
                        const element = menuItemDomRefs.current[key];
                        if (element) {
                            element.scrollIntoView({
                                behavior: 'smooth',
                                block: 'nearest'
                            });
                        }
                    }, 250);
                }
            }
        } else {
            navigate(item.route);
            if (onMenuSelect) onMenuSelect();
        }
    };

    const clearSearch = () => setSearchTerm('');

    const itemMatchesSearch = (item) => {
        const menuText = getMenuText(item.menu);
        if (menuText.toLowerCase().includes(searchTerm.toLowerCase())) return true;
        if (item.children) return item.children.some(child => itemMatchesSearch(child));
        return false;
    };

    const renderMenuItems = (items, level = 0, parentIndex = '') => {
        return items.map((item) => {
            const itemKey = generateKey(item, parentIndex, level);
            const hasChildren = item.children && item.children.length > 0;
            const paddingLeft = level === 0 ? 2 : 2 + (level * 2);
            const active = isActive(item);
            const menuText = getMenuText(item.menu);

            return (
                <React.Fragment key={itemKey}>
                    <ListItem
                        ref={(el) => {
                            if (el) {
                                menuItemDomRefs.current[itemKey] = el;
                            } else {
                                delete menuItemDomRefs.current[itemKey];
                            }
                            if (active) {
                                activeItemRef.current = el;
                            }
                        }}
                        disablePadding
                        sx={{ display: 'block', mb: 0.5, position: 'relative' }}
                    >
                        <Tooltip title={sidebarCollapsed && level === 0 ? menuText : ''} placement="right" enterDelay={500} arrow>
                            <ListItemButton
                                selected={active}
                                onClick={() => handleClick(itemKey, item)}
                                sx={{
                                    borderRadius: theme.shape.borderRadius,
                                    mx: 1,
                                    justifyContent: sidebarCollapsed && level === 0 ? 'center' : 'flex-start',
                                    minHeight: 44,
                                    pl: sidebarCollapsed && level === 0 ? 0 : paddingLeft,
                                    position: 'relative',
                                    transition: 'all 0.2s ease',
                                    '&::before': active && !sidebarCollapsed ? {
                                        content: '""',
                                        position: 'absolute',
                                        left: 0,
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        width: 3,
                                        height: 32,
                                        backgroundColor: theme.palette.primary.main,
                                        borderRadius: '0 4px 4px 0',
                                    } : {},
                                    '&.Mui-selected': {
                                        backgroundColor: alpha(theme.palette.primary.main, 0.12),
                                        color: theme.palette.primary.main,
                                        '&:hover': {
                                            backgroundColor: alpha(theme.palette.primary.main, 0.18),
                                        },
                                        '& .MuiListItemIcon-root': {
                                            color: theme.palette.primary.main,
                                        },
                                    },
                                    '&:hover': {
                                        backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                        '& .MuiListItemIcon-root': {
                                            color: theme.palette.primary.main,
                                        },
                                        '& .MuiListItemText-primary': {
                                            color: theme.palette.primary.main,
                                        },
                                        transform: 'translateX(4px)',
                                    },
                                }}
                            >
                                <ListItemIcon sx={{
                                    minWidth: sidebarCollapsed && level === 0 ? 'auto' : 40,
                                    color: active ? theme.palette.primary.main : 'text.secondary',
                                    justifyContent: 'center',
                                    transition: 'color 0.2s ease',
                                }}>
                                    {item.icon ? <item.icon /> : <FiberManualRecord sx={{ fontSize: 12 }} />}
                                </ListItemIcon>

                                {(!sidebarCollapsed || level > 0) && (
                                    <>
                                        <ListItemText
                                            primary={menuText}
                                            primaryTypographyProps={{
                                                variant: 'body2',
                                                fontSize: level > 1 ? '0.8125rem' : '0.875rem',
                                                fontWeight: active ? 600 : (level > 1 ? 400 : 500),
                                            }}
                                            sx={{
                                                ml: level > 0 ? 0.5 : 0,
                                                '& .MuiTypography-root': {
                                                    transition: 'color 0.2s ease',
                                                    color: active ? theme.palette.primary.main : 'text.primary',
                                                },
                                            }}
                                        />
                                        {hasChildren && !sidebarCollapsed && (
                                            openStates[itemKey] ?
                                                <ExpandLess sx={{ color: active ? theme.palette.primary.main : 'text.secondary', fontSize: 18 }} /> :
                                                <ExpandMore sx={{ color: active ? theme.palette.primary.main : 'text.secondary', fontSize: 18 }} />
                                        )}
                                    </>
                                )}
                            </ListItemButton>
                        </Tooltip>

                        {!sidebarCollapsed && hasChildren && (
                            <Collapse in={openStates[itemKey]} timeout="auto">
                                <List component="div" disablePadding dense sx={{ pl: 2 }}>
                                    {item.children
                                        .filter(child => searchTerm === '' || itemMatchesSearch(child))
                                        .map((child) => renderMenuItems([child], level + 1, itemKey))}
                                </List>
                            </Collapse>
                        )}
                    </ListItem>
                </React.Fragment>
            );
        });
    };

    const filteredMainItems = searchTerm ? MenuItemLists.filter(item => itemMatchesSearch(item)) : MenuItemLists;

    return (
        <Stack sx={{
            flexGrow: 1,
            p: sidebarCollapsed ? 1 : 1.5,
            justifyContent: 'space-between',
            height: '100%',
            position: 'relative',
            overflow: 'hidden',
        }}>
            <Collapse in={!sidebarCollapsed} timeout={200} unmountOnExit={false}>
                <Box sx={{ width: '100%', pb: 2, mb: 1, borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.15)}` }}>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder={t('search', 'Search menu...')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchRounded fontSize="small" sx={{ color: 'text.secondary' }} />
                                </InputAdornment>
                            ),
                            endAdornment: searchTerm && (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={clearSearch}>
                                        <ClearRounded fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: theme.shape.borderRadius,
                                backgroundColor: alpha(theme.palette.background.default, 0.8),
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    backgroundColor: 'background.paper',
                                    borderColor: theme.palette.primary.main,
                                },
                                '&.Mui-focused': {
                                    borderColor: theme.palette.primary.main,
                                    boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`,
                                },
                            },
                        }}
                    />
                </Box>
            </Collapse>

            <List component="nav" sx={{ py: 0, flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
                {renderMenuItems(filteredMainItems)}
                {!sidebarCollapsed && filteredMainItems.length === 0 && searchTerm && (
                    <ListItem>
                        <ListItemText primary={t('noResultsFound', 'No menu items found')} primaryTypographyProps={{ variant: 'body2', color: 'text.secondary', textAlign: 'center', fontStyle: 'italic' }} />
                    </ListItem>
                )}
            </List>
        </Stack>
    );
}
